export const LOGIN_TO_DASHBOARD_TIMING = 'login_to_dashboard';
export const AUTH_SUBMIT_MARK = 'tio:auth-submit';
export const DASHBOARD_RENDERED_MARK = 'tio:dashboard-rendered';
export const LOGIN_TO_DASHBOARD_MEASURE = 'tio:login-to-dashboard';

/**
 * Records the moment the user submits credentials or an MFA code, as the start of the
 * login → dashboard measurement. A later submit in the same flow (the MFA step after the
 * password form) replaces the earlier mark, so the measurement always spans the last
 * user action before the dashboard rather than the time spent typing a code.
 */
export function markAuthSubmit(): void {
  if (typeof performance === 'undefined') return;
  performance.clearMarks(AUTH_SUBMIT_MARK);
  performance.mark(AUTH_SUBMIT_MARK);
}

/** Routes on which a pending submit mark stays valid: the login flow itself and its destination. */
const AUTH_FLOW_ROUTE = /^(login|post-login(\.|$)|authenticated\.dashboard$)/;

/**
 * Drops a pending submit mark when authentication lands somewhere other than the dashboard,
 * so a later dashboard visit does not report a span that includes unrelated navigation.
 * Call from the router's `routeDidChange` handler; the login and post-login routes keep the
 * mark because the user is still inside the flow being measured.
 *
 * @param routeName the route that just became current.
 */
export function settleAuthSubmitForRoute(routeName: string): void {
  if (typeof performance === 'undefined' || AUTH_FLOW_ROUTE.test(routeName)) return;
  performance.clearMarks(AUTH_SUBMIT_MARK);
}

/**
 * Records that the dashboard has rendered. When a submit mark is pending, records the
 * `tio:login-to-dashboard` performance measure from that submit to now and clears the mark,
 * so later dashboard visits in the same session do not report again.
 *
 * @returns the measured duration in milliseconds, or `null` when no submit was pending.
 */
export function markDashboardRendered(): number | null {
  if (typeof performance === 'undefined') return null;

  const [submit] = performance.getEntriesByName(AUTH_SUBMIT_MARK, 'mark');
  if (!submit) return null;

  performance.mark(DASHBOARD_RENDERED_MARK);
  const measure = performance.measure(
    LOGIN_TO_DASHBOARD_MEASURE,
    AUTH_SUBMIT_MARK,
    DASHBOARD_RENDERED_MARK
  );
  performance.clearMarks(AUTH_SUBMIT_MARK);

  return measure?.duration ?? performance.now() - submit.startTime;
}
